import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ThemeAccent = 'orange' | 'teal' | 'violet'

export const THEME_ACCENTS: { value: ThemeAccent; label: string; dot: string }[] = [
  { value: 'orange', label: 'Shopbay orange', dot: '#ff6a00' },
  { value: 'teal', label: 'Workshop teal', dot: '#0d9488' },
  { value: 'violet', label: 'Deep violet', dot: '#7c3aed' },
]

interface ThemeState {
  theme: ThemeMode
  accent: ThemeAccent
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: ThemeMode) => void
  setAccent: (accent: ThemeAccent) => void
  _resolveTheme: () => void
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: ThemeMode): 'light' | 'dark' {
  return theme === 'system' ? getSystemTheme() : theme
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      accent: 'orange',
      resolvedTheme: getSystemTheme(),
      setTheme: (theme) => {
        set({ theme, resolvedTheme: resolveTheme(theme) })
      },
      setAccent: (accent) => {
        set({ accent })
      },
      _resolveTheme: () => {
        set({ resolvedTheme: resolveTheme(get().theme) })
      },
    }),
    {
      name: 'shopbay-theme',
      version: 3,
      migrate: (persistedState: any) => {
        if (!persistedState) return { theme: 'dark', accent: 'orange' }
        // v2 stored only `theme` and forced 'system' to 'dark'; v3 keeps
        // 'system' as a real choice and adds `accent` (default orange).
        const theme =
          persistedState.theme === 'light' ||
          persistedState.theme === 'dark' ||
          persistedState.theme === 'system'
            ? persistedState.theme
            : 'dark'
        const accent =
          persistedState.accent === 'teal' || persistedState.accent === 'violet'
            ? persistedState.accent
            : 'orange'
        return { ...persistedState, theme, accent }
      },
      partialize: (state) => ({ theme: state.theme, accent: state.accent }),
    }
  )
)
